'use client';

/**
 * Activations Showcase Page
 * Gallery of case studies with brand storytelling
 */

import Link from 'next/link';
import Image from 'next/image';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Eye, MapPin, Calendar, ArrowRight } from 'lucide-react';
import { activations } from '@/lib/data';

export default function ActivationsPage() {
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      sports: 'bg-primary text-primary-foreground',
      music: 'bg-secondary text-secondary-foreground',
      fashion: 'bg-accent text-accent-foreground',
      culture: 'bg-gradient-electric text-white',
    };
    return colors[category] || 'bg-muted';
  };

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <section className="relative py-24 bg-gradient-to-br from-primary/10 via-background to-secondary/10 overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <h1 className="text-5xl md:text-7xl font-black mb-6 uppercase">
              Our <span className="text-gradient-electric">Activations</span>
            </h1>
            <p className="text-xl md:text-2xl text-foreground/80 mb-8">
              Culture-forward experiential retail activations that drive brand
              awareness and create authentic connections with consumers.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-6 h-6 text-primary" />
                <span className="text-lg font-bold">+58% Avg Brand Lift</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="w-6 h-6 text-secondary" />
                <span className="text-lg font-bold">125K+ Attendees</span>
              </div>
              <div className="flex items-center space-x-2">
                <Eye className="w-6 h-6 text-accent" />
                <span className="text-lg font-bold">42M+ Social Reach</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Activations Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activations.map((activation, index) => (
              <motion.div
                key={activation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link href={`/activations/${activation.id}`}>
                  <Card className="group overflow-hidden card-hover border-2 border-transparent hover:border-primary h-full">
                    {/* Image */}
                    <div className="relative h-72">
                      <Image
                        src={activation.image}
                        alt={activation.title}
                        fill
                        className="object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <Badge className={getCategoryColor(activation.category)}>
                          {activation.category}
                        </Badge>
                      </div>

                      {/* Brand + Title Overlay */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-sm font-bold text-primary mb-1">
                          {activation.brand}
                        </p>
                        <h3 className="text-2xl font-black text-foreground line-clamp-2 group-hover:text-gradient-electric transition-all">
                          {activation.title}
                        </h3>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                      <p className="text-muted-foreground line-clamp-2">
                        {activation.description}
                      </p>

                      {/* Meta Info */}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <MapPin className="w-4 h-4" />
                          <span>{activation.location}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{activation.date}</span>
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
                        {activation.metrics.attendees && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">
                              Attendees
                            </p>
                            <p className="text-lg font-black text-primary">
                              {(activation.metrics.attendees / 1000).toFixed(1)}K
                            </p>
                          </div>
                        )}
                        {activation.metrics.brandAwarenessLift && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">
                              Brand Lift
                            </p>
                            <p className="text-lg font-black text-gradient-electric">
                              {activation.metrics.brandAwarenessLift}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* View Case Study Link */}
                      <Button
                        variant="ghost"
                        className="w-full group/btn justify-between"
                      >
                        View Case Study
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <Card className="relative overflow-hidden bg-gradient-sunset p-12 md:p-16 text-center">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black mb-6 text-white uppercase">
                Let's Create Your Activation
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Partner with us to design a culture-forward experience that
                drives real brand impact.
              </p>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="text-lg px-8 h-14 shadow-2xl"
              >
                <Link href="/partnerships">
                  Start a Partnership
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
